import { KText } from '@/ui-library/components';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import './availableAnimation.scss';

const HOVER_TIME_INTERVAL = 15; // in minutes
const LINE_HEIGHT_PX = 18; // approximate line height for slot text (used for dynamic noOfLines)

interface AvailableEventContentProps {
  eventInfo: { event: any }; // Event with start and end times
  createNewEvent: any; // Callback to create a new event
  interviewDataRef?: any; // Duration in minutes for each slot (default: 15)
  registerForUpdates: any;
  timezone: string;
  /** Custom content. Can be a node or a render prop that receives noOfLines and isNarrow for dynamic truncation (full content when space allows). */
  slotContent?:
    | React.ReactNode
    | ((_options: {
        _noOfLines: number;
        _isNarrow: boolean;
      }) => React.ReactNode);
  /** Tooltip content when hovering the slot. When provided, wraps content in Tooltip */
  tooltipLabel?: React.ReactNode;
  /** Optional Chakra props for the slot content wrapper (e.g. p="2px 4px") */
  contentBoxProps?: Record<string, unknown>;
}

/**
 * Component to display an available time slot and allow selection of sub-slots
 * with custom duration, 5-minute interval hover behavior, and an improved UI
 * featuring a tooltip for slot details.
 */
export const ManualAvailableContent: React.FC<AvailableEventContentProps> = ({
  eventInfo,
  createNewEvent,
  interviewDataRef,
  registerForUpdates: _registerForUpdates,
  timezone,
  slotContent,
  tooltipLabel,
  contentBoxProps,
}) => {
  const { event } = eventInfo;
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentBoxRef = useRef<HTMLDivElement>(null);
  const [noOfLines, setNoOfLines] = useState(3); // allow a few lines until measured
  const [isNarrow, setIsNarrow] = useState(false); // true when slot is too narrow (e.g. 1/4 width)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [selectedSlot, setSelectedSlot] = useState<{
    start: moment.Moment;
    end: moment.Moment;
    isSameDay: boolean;
  } | null>(null);

  // Extract duration from interviewDataRef
  const { duration } = interviewDataRef.current?.bookedSlot || {};

  // Store the event id to track when the event changes
  const [eventId, setEventId] = useState<string>(event.id || '');
  const [showTooltip, setShowTooltip] = useState(true); // default true so tooltip shows; hide only when we detect no truncation

  // Reset selected slot when event or timezone changes
  useEffect(() => {
    const currentEventId = event.id || '';
    if (currentEventId !== eventId) {
      setEventId(currentEventId);
      setSelectedSlot(null);
    }
  }, [event.id, eventId, timezone]);

  // Clear the hover overlay on scroll. Browsers fire synthetic mousemoves during
  // wheel-scroll (the element under a stationary cursor moves), and between scroll
  // frames getBoundingClientRect() returns mid-animation values, which makes the
  // slot calculation jitter and occasionally hit the end-of-event clamp, growing
  // the grey area. Clearing on scroll forces a stable recompute on the next real
  // mousemove.
  useEffect(() => {
    const handleScroll = () => {
      setSelectedSlot(null);
      setMousePos(null);
    };
    // Capture phase so we catch FullCalendar's internal scroller as well as window
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('wheel', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('wheel', handleScroll);
    };
  }, []);

  // Measure content area and set noOfLines/isNarrow so we show full content when space allows
  const NARROW_WIDTH_THRESHOLD = 40; // px — below this, hide content and rely on tooltip
  useEffect(() => {
    if (typeof slotContent !== 'function') return;
    const el = contentBoxRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height: h, width: w } = entry.contentRect;
        const lines = Math.max(1, Math.floor(h / LINE_HEIGHT_PX));
        setNoOfLines(lines);
        setIsNarrow(w < NARROW_WIDTH_THRESHOLD);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [slotContent]);

  // Check truncation on hover - only hide tooltip when content is fully visible
  const checkTruncation = useCallback(() => {
    // Always show tooltip when content is hidden due to narrow width
    if (isNarrow) {
      setShowTooltip(true);
      return;
    }
    const el = contentBoxProps ? contentBoxRef.current : containerRef.current;
    if (!el) return;
    const isOverflowed = (node: Element) =>
      node.scrollHeight > node.clientHeight ||
      node.scrollWidth > node.clientWidth;
    const truncated =
      isOverflowed(el) ||
      Array.from(el.querySelectorAll('*')).some(isOverflowed);
    setShowTooltip(truncated);
  }, [contentBoxProps, isNarrow]);

  // Format time for display (e.g., "3:00 PM")
  const formatTime = useCallback((date: moment.Moment) => {
    return date.format('h:mm A'); // Ensures uppercase AM/PM
  }, []);

  // Calculate hover slot based on mouse position with 15-minute increments
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation(); // Prevent FullCalendar interference
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const totalHeight = rect.height;

      setMousePos({ x: e.clientX, y: e.clientY });

      if (mouseY < 0 || mouseY > totalHeight) {
        setSelectedSlot(null);
        return;
      }

      // Use the instance's actual visible times (handles multi-day events correctly)
      const eventStart = moment(event.start).tz(timezone);
      const eventEnd = moment(event.end).tz(timezone);
      const isSingleDayEvent = eventStart.isSame(eventEnd, 'day');

      let visibleStart = eventStart;
      let visibleEnd = eventEnd;

      // For multi-day events, calculate only the visible portion on this day
      if (!isSingleDayEvent) {
        // Determine which day column we're rendering in via FullCalendar's DOM
        const colEl = container.closest(
          '.fc-timegrid-col'
        ) as HTMLElement | null;
        const dateAttr = colEl?.getAttribute('data-date');

        let dayStart: moment.Moment;
        if (dateAttr) {
          dayStart = moment.tz(dateAttr, timezone).startOf('day');
        } else {
          // Fallback: use FullCalendar's segment flags if available
          const evInfo = eventInfo as { isStart?: boolean; isEnd?: boolean };
          if (evInfo?.isEnd && !evInfo?.isStart) {
            // Last day segment
            dayStart = eventEnd.clone().startOf('day');
          } else {
            // First day segment or unknown
            dayStart = eventStart.clone().startOf('day');
          }
        }
        const dayEnd = dayStart.clone().endOf('day');

        // Clip the event to the current day's bounds
        visibleStart = moment.max(eventStart, dayStart);
        visibleEnd = moment.min(eventEnd, dayEnd);
      }

      // Total duration in minutes of the visible portion
      const totalDurationMinutes = visibleEnd.diff(visibleStart, 'minutes');

      // Time in minutes corresponding to mouse position
      const minutesFromStart = Math.max(
        0,
        Math.min(
          totalDurationMinutes,
          (mouseY / totalHeight) * totalDurationMinutes
        )
      );

      // Round down to nearest 15-minute interval
      const roundedMinutes =
        Math.floor(minutesFromStart / HOVER_TIME_INTERVAL) *
        HOVER_TIME_INTERVAL;

      // Create slot start time with rounding, from the visible start
      const slotStart = moment(visibleStart)
        .add(roundedMinutes, 'minutes')
        .seconds(0)
        .milliseconds(0);

      // Calculate slot end
      const slotEnd = moment(slotStart).add(duration, 'minutes');

      // Check if the slot would exceed event end
      if (slotEnd.isAfter(eventEnd)) {
        // Adjust to show last possible slot within the event bounds
        const finalEnd = moment(eventEnd);
        const finalStart = moment(eventEnd).subtract(duration, 'minutes');

        // Ensure finalStart doesn't go before event start
        if (finalStart.isBefore(eventStart)) {
          const adjustedStart = moment(eventStart);
          const adjustedEnd = moment(eventStart).add(duration, 'minutes');
          setSelectedSlot({
            start: adjustedStart,
            end: adjustedEnd,
            isSameDay: adjustedStart.isSame(adjustedEnd, 'day'),
          });
        } else {
          setSelectedSlot({
            start: finalStart,
            end: finalEnd,
            isSameDay: finalStart.isSame(finalEnd, 'day'),
          });
        }
      } else {
        // Normal case - slot is within event bounds
        setSelectedSlot({
          start: slotStart,
          end: slotEnd,
          isSameDay: slotStart.isSame(slotEnd, 'day'),
        });
      }
    },
    [event.start, event.end, duration, timezone, eventInfo]
  );

  // Handle slot selection on click
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation(); // Prevent FullCalendar click interference
      if (selectedSlot) {
        const eventObj = {
          id: uuidv4(),
          start: selectedSlot.start.format('YYYY-MM-DDTHH:mm:ss'),
          end: selectedSlot.end.format('YYYY-MM-DDTHH:mm:ss'),
        };
        createNewEvent(eventObj);
      }
    },
    [selectedSlot, createNewEvent]
  );

  // Calculate position and height for the hover overlay
  const getOverlayStyles = useMemo(() => {
    if (!selectedSlot) return { display: 'none' };

    const eventStart = moment(event.start).tz(timezone);
    const eventEnd = moment(event.end).tz(timezone);
    const isSingleDayEvent = eventStart.isSame(eventEnd, 'day');

    let visibleStart = eventStart;
    let visibleEnd = eventEnd;

    // For multi-day events, use the day containing the selected slot
    if (!isSingleDayEvent) {
      const slotDayStart = selectedSlot.start.clone().startOf('day');
      const slotDayEnd = selectedSlot.start.clone().endOf('day');
      visibleStart = moment.max(eventStart, slotDayStart);
      visibleEnd = moment.min(eventEnd, slotDayEnd);
    }

    const visibleDuration = visibleEnd.diff(visibleStart, 'minutes');
    const slotStartOffset = selectedSlot.start.diff(visibleStart, 'minutes');
    const slotDuration = moment
      .min(selectedSlot.end, visibleEnd)
      .diff(selectedSlot.start, 'minutes');

    const topPercentage = Math.max(
      0,
      (slotStartOffset / visibleDuration) * 100
    );
    const heightPercentage = Math.min(
      100,
      (slotDuration / visibleDuration) * 100
    );

    return {
      top: `${topPercentage}%`,
      height: `${heightPercentage}%`,
      opacity: 1,
      zIndex: 10,
    };
  }, [selectedSlot, event.start, event.end, timezone]);

  const isMinimalView = duration <= 30;
  const startTime = selectedSlot ? formatTime(selectedSlot.start) : '';
  const endTime = selectedSlot ? formatTime(selectedSlot.end) : '';

  // Determine if we need to show date for cross-day slots
  const showDateWithTime = selectedSlot ? !selectedSlot.isSameDay : false;
  const timeDisplay = showDateWithTime
    ? `${startTime} - ${endTime}`
    : startTime;

  const defaultContent = (
    <KText color="primary-900" variant="small_text" fontWeight="650">
      Available
    </KText>
  );

  const resolvedSlotContent =
    typeof slotContent === 'function'
      ? slotContent({ _noOfLines: noOfLines, _isNarrow: isNarrow })
      : (slotContent ?? defaultContent);

  const innerContent = (
    <>
      {contentBoxProps ? (
        <Box
          ref={contentBoxRef}
          {...contentBoxProps}
          overflow="hidden"
          minW={0}
          h="100%"
        >
          {resolvedSlotContent}
        </Box>
      ) : (
        resolvedSlotContent
      )}
      {/* Hover overlay for selected slot with improved styling and tooltip */}
      {selectedSlot && (
        <Flex
          ref={overlayRef}
          position="absolute"
          left="0"
          right="0"
          bg="primary-25"
          flexDir={isMinimalView ? 'row' : 'column'}
          p={isMinimalView ? '6px' : '8px'}
          borderRadius="6px"
          w="100%"
          alignItems={isMinimalView ? 'center' : 'start'}
          gap="3px"
          style={getOverlayStyles}
          pointerEvents="none"
          boxShadow="0px 71px 29px rgba(114, 131, 224, 0.01), 0px 40px 24px rgba(114, 131, 224, 0.05), 0px 18px 18px rgba(114, 131, 224, 0.09), 0px 4px 10px rgba(114, 131, 224, 0.1)"
        >
          <Icon color="secondary-700" as={PlusCircle} w="14px" h="14px" />
          {!isMinimalView && (
            <KText variant="small_text" fontWeight="650">
              Select slot
            </KText>
          )}
          <KText
            variant="small_text"
            textTransform="uppercase"
            fontWeight="550"
          >
            {timeDisplay}
          </KText>
        </Flex>
      )}
    </>
  );

  const container = (
    <Box
      ref={containerRef}
      position="relative"
      h="100%"
      w="100%"
      onMouseEnter={tooltipLabel ? checkTruncation : undefined}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => {
        setSelectedSlot(null);
        setMousePos(null);
        if (tooltipLabel) setShowTooltip(true);
      }}
      onClick={handleClick}
      cursor="pointer"
      borderColor="none"
      zIndex={1}
    >
      {innerContent}
    </Box>
  );

  if (tooltipLabel) {
    return (
      <>
        {container}
        {showTooltip &&
          mousePos &&
          createPortal(
            <Box
              position="fixed"
              left={`${mousePos.x + 12}px`}
              top={`${mousePos.y + 12}px`}
              bg="gray.800"
              color="white"
              px="8px"
              py="4px"
              borderRadius="4px"
              fontSize="xs"
              pointerEvents="none"
              zIndex={9999}
              boxShadow="md"
              maxW="200px"
            >
              {tooltipLabel}
            </Box>,
            document.body
          )}
      </>
    );
  }
  return container;
};
